import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { 
  DialogueSimulatorStore,
  SavedSimulation,
  DialogueTurn,
  DialogueParticipant,
  CharacterRelationship,
  SceneSettings,
  ScenePreset,
  DialogueSimulationResult,
  CompressionResult
} from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

// デフォルト制限値
const DEFAULT_MAX_SIMULATIONS = 100
const DEFAULT_MAX_DIALOGUES_PER_SIMULATION = 500

// デフォルトプリセット
const DEFAULT_PRESETS: ScenePreset[] = [
  {
    id: 'preset_school_afternoon',
    name: '学校の放課後',
    description: '放課後の教室で友人と過ごす日常的なシーン',
    settings: {
      location: '学校の教室',
      situation: '放課後の自由時間',
      mood: 'casual',
      timeOfDay: 'afternoon',
      weather: '晴れ',
      backgroundInfo: '授業が終わり、部活動まで時間がある'
    },
    tags: ['学園', '日常', '青春'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'preset_adventurer_guild',
    name: '冒険者ギルド',
    description: '冒険者たちが集まる酒場での情報交換',
    settings: {
      location: '冒険者ギルドの酒場',
      situation: '依頼の相談',
      mood: 'casual',
      timeOfDay: 'evening',
      weather: undefined,
      backgroundInfo: '新しいクエストについて相談している'
    },
    tags: ['ファンタジー', '冒険', 'ギルド'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'preset_night_bar',
    name: '深夜のバー',
    description: '大人の雰囲気漂うバーでの密談',
    settings: {
      location: '高級バーの個室',
      situation: '密談',
      mood: 'mysterious',
      timeOfDay: 'night',
      weather: '雨',
      backgroundInfo: '重要な取引について話し合っている'
    },
    tags: ['大人', 'ミステリー', '夜'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'preset_battle_field',
    name: '戦場',
    description: '緊迫した戦闘シーン',
    settings: {
      location: '荒野の戦場',
      situation: '敵との対峙',
      mood: 'action',
      timeOfDay: 'dawn',
      weather: '曇り',
      backgroundInfo: '決戦を前に作戦を確認している'
    },
    tags: ['バトル', 'アクション', '緊張'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
]

// ユーティリティ関数
function generateId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
}

// タイトル抽出
function extractTitle(firstMessage: string): string {
  const maxLength = 30
  const cleaned = firstMessage.replace(/[\n\r]/g, ' ').trim()
  if (cleaned.length <= maxLength) return cleaned
  return cleaned.substring(0, maxLength) + '...'
}

// テキストフォーマット
function formatSimulationAsText(simulation: SavedSimulation): string {
  let text = `# ${simulation.title}\n\n`
  text += `作成日時: ${new Date(simulation.createdAt).toLocaleString()}\n`
  text += `モード: ${simulation.result.mode === 'roleplay' ? 'ロールプレイ' : '自動生成'}\n\n`
  
  text += `## シーン設定\n`
  text += `場所: ${simulation.result.sceneSettings.location}\n`
  text += `状況: ${simulation.result.sceneSettings.situation}\n`
  text += `雰囲気: ${simulation.result.sceneSettings.mood}\n\n`
  
  text += `## 参加キャラクター\n`
  simulation.result.participants.forEach(p => {
    text += `- ${p.characterName} (${p.role === 'player' ? 'プレイヤー' : 'NPC'})\n`
  })
  text += '\n'
  
  text += `## 対話内容\n\n`
  simulation.result.dialogues.forEach(turn => {
    text += `${turn.characterName}: ${turn.content}\n`
    if (turn.action) text += `  [${turn.action}]\n`
    if (turn.thought) text += `  (${turn.thought})\n`
    text += '\n'
  })
  
  return text
}

// 脚本フォーマット
function formatSimulationAsScreenplay(simulation: SavedSimulation): string {
  let text = `タイトル: ${simulation.title}\n\n`
  text += `○ ${simulation.result.sceneSettings.location}\n\n`
  
  simulation.result.dialogues.forEach(turn => {
    text += `${turn.characterName}\n`
    if (turn.action) text += `（${turn.action}）\n`
    text += `「${turn.content}」\n`
    if (turn.thought) text += `N（${turn.thought}）\n`
    text += '\n'
  })
  
  return text
}

// ダウンロード処理
function downloadFile(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export const useDialogueSimulatorStore = create<DialogueSimulatorStore>()(
  persist(
    (set, get) => ({
      // 基本状態
      simulations: [],
      activeSimulation: null,
      isLoading: false,
      error: null,
      isHydrated: false,
      
      // 設定状態
      currentParticipants: [],
      currentRelationships: [],
      currentScene: null,
      defaultSettings: {
        responseLength: 'medium',
        includeActions: true,
        includeThoughts: false,
        emotionalIntensity: 5
      },
      
      // キャッシュ
      relationshipCache: {},
      characterIconCache: {},
      characterPortraitCache: {},
      
      // 表示オプション
      displayOptions: {
        showAvatars: true,
        showPortraits: false,
        portraitPosition: 'right' as const
      },
      
      // プリセット
      scenePresets: DEFAULT_PRESETS,
      
      // シミュレーション管理
      createSimulation: (title) => {
        const simulation: SavedSimulation = {
          id: generateId(),
          title: title || `対話シミュレーション_${new Date().toLocaleString()}`,
          result: {
            simulationId: generateId(),
            mode: 'roleplay',
            dialogues: [],
            sceneSettings: get().currentScene || {
              location: '',
              situation: '',
              mood: 'casual'
            },
            participants: get().currentParticipants,
            relationships: get().currentRelationships,
            metadata: {
              totalTurns: 0,
              generatedAt: new Date().toISOString(),
              modelUsed: 'gemini-2.5-pro'
            }
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          continuationCount: 0
        }
        
        set(state => ({
          simulations: [...state.simulations, simulation],
          activeSimulation: simulation
        }))
      },
      
      loadSimulation: (simulationId) => {
        const simulation = get().simulations.find(s => s.id === simulationId)
        if (simulation) {
          set({
            activeSimulation: simulation,
            currentParticipants: simulation.result.participants,
            currentRelationships: simulation.result.relationships,
            currentScene: simulation.result.sceneSettings
          })
        }
      },
      
      deleteSimulation: (simulationId) => {
        set(state => ({
          simulations: state.simulations.filter(s => s.id !== simulationId),
          activeSimulation: state.activeSimulation?.id === simulationId ? null : state.activeSimulation
        }))
      },
      
      duplicateSimulation: (simulationId) => {
        const simulation = get().simulations.find(s => s.id === simulationId)
        if (!simulation) return
        
        const duplicated: SavedSimulation = {
          ...simulation,
          id: generateId(),
          title: `${simulation.title} (コピー)`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
        
        set(state => ({
          simulations: [...state.simulations, duplicated]
        }))
      },
      
      updateSimulationTitle: (simulationId, firstMessage) => {
        const title = extractTitle(firstMessage)
        set(state => ({
          simulations: state.simulations.map(s =>
            s.id === simulationId ? { ...s, title } : s
          ),
          activeSimulation: state.activeSimulation?.id === simulationId
            ? { ...state.activeSimulation, title }
            : state.activeSimulation
        }))
      },
      
      // お気に入り機能
      toggleFavorite: (simulationId) => {
        set(state => ({
          simulations: state.simulations.map(s =>
            s.id === simulationId 
              ? { ...s, isFavorite: !s.isFavorite }
              : s
          ),
          activeSimulation: state.activeSimulation?.id === simulationId
            ? { ...state.activeSimulation, isFavorite: !state.activeSimulation.isFavorite }
            : state.activeSimulation
        }))
      },
      
      // 参加者管理
      addParticipant: (character) => {
        set(state => ({
          currentParticipants: [...state.currentParticipants, character]
        }))
      },
      
      removeParticipant: (characterId) => {
        set(state => ({
          currentParticipants: state.currentParticipants.filter(p => p.characterId !== characterId),
          currentRelationships: state.currentRelationships.filter(
            r => r.character1Id !== characterId && r.character2Id !== characterId
          )
        }))
      },
      
      setPlayerCharacter: (characterId) => {
        set(state => ({
          currentParticipants: state.currentParticipants.map(p => ({
            ...p,
            role: p.characterId === characterId ? 'player' : 'npc'
          }))
        }))
      },
      
      updateParticipantIcon: (characterId, icon) => {
        set(state => ({
          currentParticipants: state.currentParticipants.map(p =>
            p.characterId === characterId ? { ...p, icon } : p
          ),
          characterIconCache: {
            ...state.characterIconCache,
            [characterId]: icon
          }
        }))
      },
      
      updateParticipantPortrait: (characterId, portrait) => {
        set(state => ({
          currentParticipants: state.currentParticipants.map(p =>
            p.characterId === characterId ? { ...p, portrait } : p
          ),
          characterPortraitCache: {
            ...state.characterPortraitCache,
            [characterId]: portrait
          }
        }))
      },
      
      updateDisplayOptions: (options) => {
        set(state => ({
          displayOptions: {
            ...state.displayOptions,
            ...options
          }
        }))
      },
      
      // 関係性管理
      setRelationship: (relationship) => {
        const key = `${relationship.character1Id}_${relationship.character2Id}`
        set(state => ({
          currentRelationships: [
            ...state.currentRelationships.filter(
              r => !(
                (r.character1Id === relationship.character1Id && r.character2Id === relationship.character2Id) ||
                (r.character1Id === relationship.character2Id && r.character2Id === relationship.character1Id)
              )
            ),
            relationship
          ],
          relationshipCache: {
            ...state.relationshipCache,
            [key]: relationship
          }
        }))
      },
      
      updateRelationship: (char1Id, char2Id, updates) => {
        set(state => ({
          currentRelationships: state.currentRelationships.map(r =>
            (r.character1Id === char1Id && r.character2Id === char2Id) ||
            (r.character1Id === char2Id && r.character2Id === char1Id)
              ? { ...r, ...updates }
              : r
          )
        }))
      },
      
      analyzeRelationship: async () => {
        // この関数は実際にはフックから呼ばれるため、ここでは空実装
        throw new Error('analyzeRelationship should be called from hook')
      },
      
      getCachedAnalysis: (pairKey) => {
        return get().relationshipCache[pairKey]
      },
      
      setCachedAnalysis: (pairKey, relationship) => {
        set(state => ({
          relationshipCache: {
            ...state.relationshipCache,
            [pairKey]: relationship
          }
        }))
      },
      
      // シーン管理
      setScene: (scene) => {
        set({ currentScene: scene })
      },
      
      updateScene: (updates) => {
        set(state => ({
          currentScene: state.currentScene
            ? { ...state.currentScene, ...updates }
            : null
        }))
      },

      updateSceneSettings: (simulationId: string, newSceneSettings: SceneSettings) => {
        set(state => {
          const simulations = state.simulations.map(sim => {
            if (sim.id === simulationId) {
              return {
                ...sim,
                sceneSettings: newSceneSettings,
                result: {
                  ...sim.result,
                  sceneSettings: newSceneSettings
                }
              }
            }
            return sim
          })

          const activeSimulation = state.activeSimulation?.id === simulationId
            ? simulations.find(s => s.id === simulationId) || state.activeSimulation
            : state.activeSimulation

          return {
            simulations,
            activeSimulation,
            currentScene: newSceneSettings
          }
        })
      },

      generateSceneFromContext: async () => {
        // この関数は実際にはフックから呼ばれるため、ここでは空実装
        throw new Error('generateSceneFromContext should be called from hook')
      },
      
      // シーンプリセット管理
      saveScenePreset: (name, description, tags) => {
        const currentScene = get().currentScene
        if (!currentScene) {
          console.error('No scene to save as preset')
          return
        }
        
        const preset: ScenePreset = {
          id: generateId(),
          name,
          description,
          settings: { ...currentScene },
          tags,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
        
        set(state => ({
          scenePresets: [...state.scenePresets, preset]
        }))
        
        console.log('Scene preset saved:', preset)
      },
      
      loadScenePreset: (presetId) => {
        const preset = get().scenePresets.find(p => p.id === presetId)
        if (preset) {
          set({ currentScene: { ...preset.settings } })
          console.log('Scene preset loaded:', preset)
        }
      },
      
      updateScenePreset: (presetId, updates) => {
        set(state => ({
          scenePresets: state.scenePresets.map(p => 
            p.id === presetId 
              ? { 
                  ...p, 
                  ...updates,
                  settings: updates.settings ? { ...updates.settings } : p.settings,
                  updatedAt: new Date().toISOString()
                }
              : p
          )
        }))
      },
      
      deleteScenePreset: (presetId) => {
        set(state => ({
          scenePresets: state.scenePresets.filter(p => p.id !== presetId)
        }))
      },
      
      // 対話管理
      addDialogueTurn: (turn) => {
        set(state => {
          if (!state.activeSimulation) return state
          
          const updatedSimulation = {
            ...state.activeSimulation,
            result: {
              ...state.activeSimulation.result,
              dialogues: [...state.activeSimulation.result.dialogues, turn],
              metadata: {
                ...state.activeSimulation.result.metadata,
                totalTurns: state.activeSimulation.result.metadata.totalTurns + 1
              }
            },
            updatedAt: new Date().toISOString()
          }
          
          return {
            activeSimulation: updatedSimulation,
            simulations: state.simulations.map(s =>
              s.id === state.activeSimulation!.id ? updatedSimulation : s
            )
          }
        })
      },
      
      setSimulation: (result) => {
        const simulation: SavedSimulation = {
          id: generateId(),
          title: `対話_${new Date().toLocaleString()}`,
          result,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          continuationCount: 0
        }
        
        console.log('setSimulation called with:', {
          dialoguesCount: result.dialogues?.length,
          simulationId: simulation.id,
          title: simulation.title
        })
        
        set(state => {
          console.log('Current state before update:', {
            simulationsCount: state.simulations.length,
            hasActiveSimulation: !!state.activeSimulation
          })

          const newState = {
            simulations: [...state.simulations, simulation],
            activeSimulation: simulation,
            currentParticipants: result.participants,
            currentRelationships: result.relationships,
            currentScene: result.sceneSettings
          }

          console.log('New state after update:', {
            simulationsCount: newState.simulations.length,
            activeSimulationId: newState.activeSimulation?.id,
            participantsCount: newState.currentParticipants?.length
          })

          return newState
        })
      },
      
      getSimulationById: (simulationId) => {
        return get().simulations.find(s => s.id === simulationId)
      },
      
      updateSimulation: (simulationId, updates) => {
        set(state => {
          const updatedSimulations = state.simulations.map(s =>
            s.id === simulationId
              ? {
                  ...s,
                  result: {
                    ...s.result,
                    ...updates,
                    dialogues: updates.dialogues || s.result.dialogues,
                    metadata: {
                      ...s.result.metadata,
                      ...updates.metadata
                    }
                  },
                  updatedAt: new Date().toISOString()
                }
              : s
          )
          
          // activeSimulationも更新
          const updatedActiveSimulation = state.activeSimulation?.id === simulationId
            ? updatedSimulations.find(s => s.id === simulationId) || state.activeSimulation
            : state.activeSimulation
            
          return {
            simulations: updatedSimulations,
            activeSimulation: updatedActiveSimulation
          }
        })
      },
      
      incrementContinuationCount: (simulationId) => {
        set(state => ({
          simulations: state.simulations.map(s =>
            s.id === simulationId
              ? { ...s, continuationCount: s.continuationCount + 1 }
              : s
          ),
          activeSimulation: state.activeSimulation?.id === simulationId
            ? { ...state.activeSimulation, continuationCount: state.activeSimulation.continuationCount + 1 }
            : state.activeSimulation
        }))
      },
      
      // 対話編集
      editDialogueTurn: (simulationId, turnId, content) => {
        set(state => {
          const simulation = state.simulations.find(s => s.id === simulationId)
          if (!simulation) return state
          
          const updatedSimulation = {
            ...simulation,
            result: {
              ...simulation.result,
              dialogues: simulation.result.dialogues.map(t =>
                t.id === turnId
                  ? {
                      ...t,
                      content,
                      metadata: {
                        ...t.metadata,
                        editedAt: new Date().toISOString(),
                        originalContent: t.metadata?.originalContent || t.content
                      }
                    }
                  : t
              )
            },
            updatedAt: new Date().toISOString()
          }
          
          return {
            simulations: state.simulations.map(s => 
              s.id === simulationId ? updatedSimulation : s
            ),
            activeSimulation: state.activeSimulation?.id === simulationId 
              ? updatedSimulation 
              : state.activeSimulation
          }
        })
      },
      
      deleteDialogueTurn: (simulationId, turnId) => {
        set(state => {
          const simulation = state.simulations.find(s => s.id === simulationId)
          if (!simulation) return state
          
          const updatedSimulation = {
            ...simulation,
            result: {
              ...simulation.result,
              dialogues: simulation.result.dialogues.filter(t => t.id !== turnId),
              metadata: {
                ...simulation.result.metadata,
                totalTurns: simulation.result.metadata.totalTurns - 1
              }
            },
            updatedAt: new Date().toISOString()
          }
          
          return {
            simulations: state.simulations.map(s => 
              s.id === simulationId ? updatedSimulation : s
            ),
            activeSimulation: state.activeSimulation?.id === simulationId 
              ? updatedSimulation 
              : state.activeSimulation
          }
        })
      },
      
      insertDialogueTurn: (simulationId, afterTurnId, turn) => {
        set(state => {
          const simulation = state.simulations.find(s => s.id === simulationId)
          if (!simulation) return state
          
          const index = simulation.result.dialogues.findIndex(t => t.id === afterTurnId)
          if (index === -1) return state
          
          const newDialogues = [...simulation.result.dialogues]
          newDialogues.splice(index + 1, 0, turn)
          
          const updatedSimulation = {
            ...simulation,
            result: {
              ...simulation.result,
              dialogues: newDialogues,
              metadata: {
                ...simulation.result.metadata,
                totalTurns: simulation.result.metadata.totalTurns + 1
              }
            },
            updatedAt: new Date().toISOString()
          }
          
          return {
            simulations: state.simulations.map(s => 
              s.id === simulationId ? updatedSimulation : s
            ),
            activeSimulation: state.activeSimulation?.id === simulationId 
              ? updatedSimulation 
              : state.activeSimulation
          }
        })
      },
      
      deleteMessagesAfter: (simulationId, messageIndex) => {
        set(state => {
          const simulation = state.simulations.find(s => s.id === simulationId)
          if (!simulation) return state
          
          const updatedSimulation = {
            ...simulation,
            result: {
              ...simulation.result,
              dialogues: simulation.result.dialogues.slice(0, messageIndex + 1),
              metadata: {
                ...simulation.result.metadata,
                totalTurns: messageIndex + 1
              }
            },
            updatedAt: new Date().toISOString()
          }
          
          return {
            simulations: state.simulations.map(s => 
              s.id === simulationId ? updatedSimulation : s
            ),
            activeSimulation: state.activeSimulation?.id === simulationId 
              ? updatedSimulation 
              : state.activeSimulation
          }
        })
      },
      
      // エクスポート/インポート
      exportSimulation: (simulationId, format) => {
        const simulation = get().simulations.find(s => s.id === simulationId)
        if (!simulation) return
        
        let content: string
        let filename: string
        
        switch (format) {
          case 'json':
            content = JSON.stringify(simulation, null, 2)
            filename = `${simulation.title}.json`
            break
          
          case 'text':
            content = formatSimulationAsText(simulation)
            filename = `${simulation.title}.txt`
            break
          
          case 'screenplay':
            content = formatSimulationAsScreenplay(simulation)
            filename = `${simulation.title}_脚本.txt`
            break
          
          default:
            return
        }
        
        downloadFile(content, filename)
      },
      
      exportAllSimulations: () => {
        const simulations = get().simulations
        const content = JSON.stringify(simulations, null, 2)
        const filename = `対話シミュレーション_全データ_${new Date().toISOString()}.json`
        downloadFile(content, filename)
      },
      
      importSimulations: (jsonString) => {
        try {
          const imported = JSON.parse(jsonString)
          const simulations = Array.isArray(imported) ? imported : [imported]
          
          set(state => ({
            simulations: [...state.simulations, ...simulations]
          }))
        } catch (error) {
          console.error('Failed to import simulations:', error)
          set({ error: 'インポートに失敗しました' })
        }
      },
      
      // ユーティリティ
      clearCache: () => {
        set({
          relationshipCache: {},
          characterIconCache: {}
        })
      },
      
      reset: () => {
        set({
          simulations: [],
          activeSimulation: null,
          isLoading: false,
          error: null,
          currentParticipants: [],
          currentRelationships: [],
          currentScene: null,
          relationshipCache: {},
          characterIconCache: {}
        })
      },
      
      getCharacterIcon: (characterId) => {
        if (!characterId) return undefined
        return get().characterIconCache[characterId]
      }
    }),
    {
      name: 'dialogue-simulator-storage',
      storage: createIndexedDBStorage('dialogue-simulator-storage'),
      partialize: (state) => ({
        simulations: state.simulations.slice(-DEFAULT_MAX_SIMULATIONS),
        defaultSettings: state.defaultSettings,
        characterIconCache: state.characterIconCache,
        characterPortraitCache: state.characterPortraitCache,
        displayOptions: state.displayOptions,
        scenePresets: state.scenePresets,
        currentParticipants: state.currentParticipants,  // 参加者情報も保存（アイコン含む）
        currentRelationships: state.currentRelationships,
        currentScene: state.currentScene
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isHydrated = true
          
          // デフォルトプリセットが存在しない場合は追加
          if (state.scenePresets) {
            const existingIds = state.scenePresets.map(p => p.id)
            const missingPresets = DEFAULT_PRESETS.filter(p => !existingIds.includes(p.id))
            if (missingPresets.length > 0) {
              state.scenePresets = [...state.scenePresets, ...missingPresets]
            }
          }
        }
      }
    }
  )
)